import { useUserDetailsContext } from 'context/userDetailsContext';
import { Tooltip } from 'primereact/tooltip';
import React from 'react';
import {
  MEDICAL_RECORD_AV_RESTRICTED_REASON,
  MEDICAL_RECORD_DESIGNATION_PERMISSION,
} from 'modules/FileManagerV2/constants/medicalRecordPromotion';
import { useFileManagerContext } from 'modules/FileManagerV2/context/FileManagerContext';
import { isAudioOrVideoFile } from 'modules/FileManagerV2/utils/file/isAudioOrVideoFile';
import { isPersonalInjuryPracticeArea } from 'utils/isPersonalInjuryPracticeArea';
import './FilesList.scss';

function FilesList({ filesWatcher, uploadingIndex, setValue, isClientFileManager }) {
  const { checkSubPermission } = useUserDetailsContext();
  const { caseDetails } = useFileManagerContext();
  const isCourtSettingNotice =
    checkSubPermission('ai_court_setting_notice') && !isClientFileManager && filesWatcher?.some((item) => item?.type === 'application/pdf');
  const showMedicalRecordCheckbox =
    checkSubPermission(MEDICAL_RECORD_DESIGNATION_PERMISSION) && !isClientFileManager && isPersonalInjuryPracticeArea(caseDetails);

  const removeFirstSlash = (itemPath = '') => {
    if (itemPath.charAt(0) === '/') {
      itemPath = itemPath.slice(1);
    }
    return itemPath;
  };

  const deleteFile = (index) => {
    let _files = [...filesWatcher];
    _files.splice(index, 1);
    setValue('files', _files);
  };

  const handleCourtSetting = (index) => {
    let _files = [...filesWatcher];
    _files[index].is_court_setting_notice = !_files[index].is_court_setting_notice;
    setValue('files', _files);
  };

  const handleMedicalRecord = (index) => {
    let _files = [...filesWatcher];
    const file = _files[index];
    if (isAudioOrVideoFile({ mimeType: file?.type, fileName: file?.name })) {
      return;
    }
    _files[index].is_medical_record = !_files[index].is_medical_record;
    setValue('files', _files);
  };

  return (
    <div className="file-list-container upload-files-list py-2">
      <div className="col-12">
        {filesWatcher?.map((item, index) => {
          const isAvMedicalRecordBlocked = isAudioOrVideoFile({ mimeType: item?.type, fileName: item?.name });
          const showCourtForRow = isCourtSettingNotice;
          const showMedicalForRow = true;
          const showDesignations = showCourtForRow || showMedicalForRow;

          return (
            <div className="upload-file-card" key={index} data-testid={`upload-file-card-${index}`}>
              <div className="upload-file-card__main">
                <div className="upload-file-card__identity d-flex align-items-center text-truncate">
                  <i className="icon-pdf fa-lg text-primary-dark flex-shrink-0" aria-hidden="true"></i>
                  <p className="mb-0 ms-2 text-primary-dark text-medium text-truncate">
                    {removeFirstSlash(item?.webkitRelativePath || item?.name)}
                  </p>
                </div>
                {uploadingIndex === index ? (
                  <i className="fas fa-spinner fa-spin ms-2 flex-shrink-0" aria-label="Uploading"></i>
                ) : item.completed ? (
                  <i className="far fa-check-circle ms-2 flex-shrink-0" aria-hidden="true"></i>
                ) : (
                  <button
                    type="button"
                    className="upload-file-card__remove"
                    onClick={() => deleteFile(index)}
                    aria-label={`Remove ${removeFirstSlash(item?.webkitRelativePath || item?.name)}`}
                  >
                    <i className="icon-delete icon-red" aria-hidden="true"></i>
                  </button>
                )}
              </div>

              {showDesignations && (
                <div className="upload-file-card__flags" data-testid={`upload-file-flags-${index}`}>
                  <div className="upload-file-card__flags-options">
                    <p className="mark-as-label">Mark As</p>
                    {showCourtForRow && (
                      <label
                        className={`upload-flag-option ${item?.is_court_setting_notice ? 'tag-blue' : 'tag-grey'}${
                          item?.type !== 'application/pdf' ? ' is-disabled' : ''
                        }`}
                      >
                        <Tooltip target={`.court-setting-tooltip-${index}`} position="top" className="mb-3">
                          Mark as Court Setting Notice
                        </Tooltip>
                        <input
                          type="checkbox"
                          className={`cursor-pointer court-setting-tooltip-${index}`}
                          checked={item.is_court_setting_notice || false}
                          onChange={() => handleCourtSetting(index)}
                          disabled={item?.type !== 'application/pdf'}
                          aria-label="Court setting notice"
                          data-testid={`court-setting-checkbox-${index}`}
                        />
                        <i className="pi pi-info-circle" aria-hidden="true"></i>
                        <span>Court setting notice</span>
                      </label>
                    )}
                    {showMedicalForRow && (
                      <label
                        className={`upload-flag-option ${item?.is_medical_record ? 'tag-blue' : 'tag-grey'}${
                          isAvMedicalRecordBlocked ? ' is-disabled' : ''
                        }`}
                        title={isAvMedicalRecordBlocked ? MEDICAL_RECORD_AV_RESTRICTED_REASON : undefined}
                      >
                        <Tooltip target={`.medical-record-tooltip-${index}`} position="top" className="mb-3">
                          Medical record
                        </Tooltip>
                        <input
                          type="checkbox"
                          className={`cursor-pointer medical-record-tooltip-${index}`}
                          checked={item.is_medical_record || false}
                          onChange={() => handleMedicalRecord(index)}
                          disabled={isAvMedicalRecordBlocked}
                          title={isAvMedicalRecordBlocked ? MEDICAL_RECORD_AV_RESTRICTED_REASON : 'Mark as medical record'}
                          aria-label="Medical record"
                          data-testid={`medical-record-checkbox-${index}`}
                        />
                        <i className="pi pi-info-circle" aria-hidden="true"></i>
                        <span>Medical record</span>
                      </label>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default FilesList;
